<template>
  <wrapper>

    <modal :title="`Delete ${organisation.name}`" v-if="modal.isVisible('deleteOrg')" @close="modal.hide('deleteOrg'); deletionString = null;" :show-footer="false">
      <p class="text-danger lead">This school will be permanently deleted. <br/> This action is irreversible.</p>
      <div class="input-group width-100">
        <input class="form-control width-100" name="name" :placeholder="`Enter '${organisation.name}' to confirm`" v-model="deletionString" type="text" autocomplete="off">
      </div>
      <div class="modal-footer">
        <slot name="footer"></slot>
        <loading-button type="button" class="btn btn-warning" data-dismiss="modal" @click="modal.hide('deleteOrg'); deletionString = null;">Cancel</loading-button>
        <loading-button type="button" class="btn btn-primary btn-danger" data-dismiss="modal" @click="deleteOrg" :disabled="deletionString !== organisation.name">Delete Permanently</loading-button>
      </div>
    </modal>
    <div class="challenge-organisation-page" v-if="organisation">
      <div class="row">
        <div class="col-md-6">
          <div class="box box-info mb-6">
            <div class="box-body">

              <table class="table table-striped" v-if="!isEditingSchool">
                <tbody>
                  <tr>
                    <th>Contact</th>
                    <td>{{ organisation.contact_name }} ({{ organisation.contact_job_role }})<br /><a
                        :href="'mailto:' + organisation.email">{{ organisation.email }}</a></td>
                  </tr>
                  <tr>
                    <th>Challenge Code</th>
                    <td>{{ organisation.code }}</td>
                  </tr>
                  <tr>
                    <th>Governance</th>
                    <td>{{ governanceDescription }}</td>
                  </tr>
                  <tr>
                    <th>Phase of Education</th>
                    <td>{{ educationPhases[organisation.education_phase] || 'Not set' }}</td>
                  </tr>
                  <tr>
                    <th>Region</th>
                    <td>{{ organisation.region }}</td>
                  </tr>
                  <tr>
                    <th>City</th>
                    <td>{{ organisation.city || 'Not set' }}</td>
                  </tr>
                  <tr v-if="organisation.english_hub">
                    <th>English Hub</th>
                    <td>
                      {{ organisation.english_hub.name }}
                      <span v-if="organisation.english_hub.is_referral" class="label label-info">referral</span>
                    </td>
                  </tr>
                  <tr>
                    <th>PURL</th>
                    <td>{{ organisation.joining_url }}</td>
                  </tr>
                  <tr v-if="organisation.enterprise">
                    <th>Fonetti School</th>
                    <td><router-link :to="{name: 'School', params: { schoolId: organisation.enterprise.id }}">{{ organisation.enterprise.name }}</router-link></td>
                  </tr>
                </tbody>
              </table>

              <div v-if="isEditingSchool">
                <form action="#" class="form-horizontal school-form" @submit.prevent="validateEditSchoolForm">

                  <div class="form-group" :class="{ 'has-error': errors.first('name') }">
                    <label for="name" class="col-sm-2 control-label">School Name</label>
                    <div class="col-sm-10">
                      <input type="text" class="form-control" name="name" id="name" autocomplete="off"
                        v-model="organisationEditable.name" v-validate="'required'">
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('name')">
                      {{ errors.first('name') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('contact_name') }">
                    <label for="contact_name" class="col-sm-2 control-label">Contact Name</label>
                    <div class="col-sm-10">
                      <input type="text" class="form-control" name="contact_name" id="contact_name" autocomplete="off"
                        v-model="organisationEditable.contact_name" v-validate="'required'">
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('contact_name')">
                      {{ errors.first('contact_name') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('contact_job_role') }">
                    <label for="contact_job_role" class="col-sm-2 control-label">Contact Job Role</label>
                    <div class="col-sm-10">
                      <input type="text" class="form-control" name="contact_job_role" id="contact_job_role"
                        autocomplete="off" v-model="organisationEditable.contact_job_role" v-validate="'required'">
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('contact_job_role')">
                      {{ errors.first('contact_job_role') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('email') }">
                    <label for="email" class="col-sm-2 control-label">Contact Email</label>
                    <div class="col-sm-10">
                      <input type="text" class="form-control" name="email" id="email" autocomplete="off"
                        v-model="organisationEditable.email" v-validate="{ required: true, email: true }">
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('email')">
                      {{ errors.first('email') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('country') }">
                    <label for="country" class="col-sm-2 control-label">Country</label>
                    <div class="col-sm-10">
                      <select class="form-control" name="country" id="country" autocomplete="off"
                        v-model="organisationEditable.country" v-validate="'required'" data-vv-as="Country">
                        <option v-for="country in countries" :value="country['id']" :key="country['id']">
                          {{ country['name'] }}</option>
                      </select>
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('country')">
                      {{ errors.first('country') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('postcode') }">
                    <label for="region" class="col-sm-2 control-label">Postcode</label>
                    <div class="col-sm-10">
                      <div class="input-group">
                        <input type="text" class="form-control" name="postcode" id="postcode" autocomplete="off"
                          v-model="organisationEditable.postcode" data-vv-as="postcode">
                        <span class="input-group-btn">
                          <button type="button" class="btn btn-default" @click="lookupPostcode"
                            :disabled="isLookingUpPostcode || !organisationEditable.postcode">
                            {{ isLookingUpPostcode ? 'Looking up...' : 'Look up' }}
                          </button>
                        </span>
                      </div>
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('postcode')">
                      {{ errors.first('postcode') }}
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block text-warning" v-show="postcodeNeedsLookup">
                      Postcode changed. Region and City have not been re-checked; press Look up to refresh them,
                      or edit them by hand.
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('region') }">
                    <label for="region" class="col-sm-2 control-label">Region</label>
                    <div class="col-sm-10">
                      <select v-if="regions.length > 0" class="form-control" name="region" id="region"
                        autocomplete="off" v-model="organisationEditable.region" v-validate="'required'"
                        data-vv-as="Region">
                        <optgroup :label="group['group_name'] != '' ? group['group_name'] : 'Chose a region'"
                          v-for="(group, key) in regions" :key="key">
                          <option v-for="(region, k) in group['values']" :value="region" :key="k">{{ region }}</option>
                        </optgroup>
                      </select>
                      <input v-else type="text" class="form-control" name="region" id="region" autocomplete="off"
                        v-model="organisationEditable.region" data-vv-as="region">
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('region')">
                      {{ errors.first('region') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('city') }">
                    <label for="city" class="col-sm-2 control-label">City</label>
                    <div class="col-sm-10">
                      <input type="text" class="form-control" name="city" id="city" autocomplete="off"
                        v-model="organisationEditable.city" data-vv-as="city">
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('city')">
                      {{ errors.first('city') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('school_type') }">
                    <label for="school_type" class="col-sm-2 control-label">School Type</label>
                    <div class="col-sm-10">
                      <select class="form-control" name="school_type" id="school_type" autocomplete="off"
                        v-model="organisationEditable.school_type" v-validate="'required'" data-vv-as="School Type">
                        <option v-for="(v, k) in schoolTypes" :value="k" :key="k">{{ v }}</option>
                      </select>
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('school_type')">
                      {{ errors.first('school_type') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('education_phase') }">
                    <label for="education_phase" class="col-sm-2 control-label">Phase of Education</label>
                    <div class="col-sm-10">
                      <select class="form-control" name="education_phase" id="education_phase" autocomplete="off"
                        v-model="organisationEditable.education_phase" data-vv-as="Phase of Education">
                        <option :value="null">Not set</option>
                        <option v-for="(label, value) in educationPhases" :value="value" :key="value">{{ label }}</option>
                      </select>
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger" v-show="errors.has('education_phase')">
                      {{ errors.first('education_phase') }}
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('school_governed_by_name') }"
                    v-if="['la', 'mat'].includes(organisationEditable.school_type)">
                    <label for="school_governed_by_name" class="col-sm-2 control-label"
                      v-text="organisationEditable.school_type == 'la' ? 'Which Local Authority?' : 'Which MAT?'"></label>
                    <div class="col-sm-10">
                      <input type="text" class="form-control" name="school_governed_by_name"
                        id="school_governed_by_name" autocomplete="off"
                        v-model="organisationEditable.school_governed_by_name" v-validate="'required'">
                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger"
                      v-show="errors.has('school_governed_by_name')">
                      {{ errors.first('school_governed_by_name') }}
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="english_hub" class="col-sm-2 control-label">English Hub</label>
                    <div class="col-sm-10">
                      <select class="form-control" name="english_hub" id="english_hub"
                        v-model="organisationEditable.english_hub_slug">
                        <option :value="null">None</option>
                        <option v-for="hub in englishHubs" :value="hub.slug" :key="hub.slug">{{ hub.name }}</option>
                      </select>
                    </div>
                  </div>

                  <div class="form-group" v-if="organisationEditable.english_hub_slug">
                    <div class="col-sm-10 col-sm-offset-2">
                      <label>
                        <input type="checkbox" v-model="organisationEditable.english_hub_is_referral" />
                        This hub referred the school
                      </label>
                    </div>
                  </div>

                  <div class="form-group" :class="{ 'has-error': errors.first('enterprise_id') }">
                    <label for="enterprise_id" class="col-sm-2 control-label">Fonetti School</label>
                    <div class="col-sm-10">

                      <vue-tags-input
                        id="enterprise_id"
                        v-model="fonettiSchoolSearchString"
                        :addOnlyFromAutocomplete="true"
                        placeholder="Select a school"
                        :autocomplete-items="filteredFonettiSchools"
                        :autocomplete-min-length="0"
                        :max-tags="1"
                        :tags="selectedFonettiSchool"
                        @tags-changed="newTags => selectedFonettiSchool = newTags"
                      ></vue-tags-input>

                    </div>
                    <div class="col-sm-10 col-sm-offset-2 help-block is-danger"
                      v-show="errors.has('enterprise_id')">
                      {{ errors.first('enterprise_id') }}
                    </div>
                  </div>

                  <div class="box-footer">
                    <loading-button type="submit" :isLoading="isSavingSchool">Save</loading-button>
                  </div>
                </form>
              </div>

            </div>
          </div>
          <!-- <div v-if="user.readers">
            <h3>Reader Profiles</h3>
            <div v-if="user.readers.length === 0" class="alert alert-warning">The user has not yet created any reader profiles</div>
            <box-with-icons v-for="reader in user.readers" :key="reader.id" @click="$router.push(`${user.id}/readers/${reader.id}`)" class="reader-profile__wrap">
              <div class="reader-profile">
                <img alt="avatar" :src="'/img/fonetti_avatars/' + reader.avatar + '.png'" class="avatar" />
                <div>
                  <a @click="$router.push(`${user.id}/readers/${reader.id}`)">{{ reader.name }} ({{ reader.username }})</a><br>
                  Age: {{ reader.age }}
                </div>
              </div>
            </box-with-icons>
          </div>

        -->
          <div class="box box-info mb-6">
            <div class="box-body">
              <div class="asset-header">
                <span class="asset-status">
                  Asset generation status:
                  <span class="asset-status__badge" :class="`asset-status__badge--${assetStatus}`">
                    <i v-if="assetStatus === 'generating'" class="fa fa-spinner fa-spin"></i>
                    {{ organisation.assets_generation_status }}
                  </span>
                </span>
                <loading-button @click="forceRegenAssets" class="btn-success btn-sm" :isLoading="isRegenerating">Force
                  Regenerate</loading-button>
              </div>
              <p v-if="assetGroups.length === 0" class="asset-empty">No assets generated yet.</p>
              <div v-else class="asset-list">
                <div v-for="group in assetGroups" :key="group.name" class="asset-row"
                  :class="{ 'asset-row--nested': group.nested }">
                  <span class="asset-row__name">{{ group.name }}</span>
                  <!-- Flat asset: type buttons inline on the right. -->
                  <span v-if="!group.nested" class="asset-row__links">
                    <a v-for="(item, i) in group.subgroups[0].items" :key="i" :href="item.url" target="_blank"
                      class="asset-link" :class="`asset-link--${item.type.toLowerCase()}`">
                      <i class="fa" :class="item.icon"></i>
                      {{ item.type }}
                    </a>
                  </span>
                  <!-- Nested asset: one indented sub-row per variant. -->
                  <div v-else class="asset-row__subrows">
                    <div v-for="sub in group.subgroups" :key="sub.label" class="asset-subrow">
                      <span class="asset-subrow__label">{{ sub.label }}</span>
                      <span class="asset-subrow__links">
                        <a v-for="(item, i) in sub.items" :key="i" :href="item.url" target="_blank"
                          class="asset-link" :class="`asset-link--${item.type.toLowerCase()}`">
                          <i class="fa" :class="item.icon"></i>
                          {{ item.type }}
                        </a>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="flex justify-content-end column-gap-3">
            <!-- <button class="btn btn-primary btn-flex" @click="startResetPassword">Reset Password</button> -->

            <button type="button" class="btn btn-primary" @click="startEditingSchool">Edit School Info</button>
            <confirmation-button :show-modal="showConfirmationModal['reset-password']"
              @close="showConfirmationModal['reset-password'] = false" @click="handleResetPassword"
              :hide-modal-footer="true">
              <template #button>Reset password</template>
              <template #modal>
                <div v-if="null === newPassword">
                  <p>The school will no longer be able to use their current password to login.</p>
                  <p>The new password will be displayed here for you to copy & paste.</p>
                  <input type="checkbox" id="send-password-email" v-model="sendPasswordEmail" /> <label
                    for="send-password-email">also send the new password in an email to the school contact</label>
                  <div class="flex justify-content-end">
                    <button class="btn btn-success" @click="handleResetPasswordContinue"
                      :disabled="isResetting">Continue</button>
                  </div>
                </div>
                <div v-else>
                  <p class="text-center"><b>New Password</b></p>
                  <input type="text" :value="newPassword" class="form-control font-monospace" readonly />
                  <div class="flex justify-content-end mt-4">
                    <button class="btn btn-primary"
                      @click="showConfirmationModal['reset-password'] = false">Close</button>
                  </div>
                </div>
              </template>
            </confirmation-button>
            <button class="btn btn-danger" @click="modal.show('deleteOrg')">Delete school</button>
          </div>
        </div>
      </div>
    </div>
  </wrapper>
</template>

<script>
import Wrapper from '@/components/Wrapper';
import ConfirmationButton from '@/components/utils/ConfirmationButton';
import LoadingButton from '@/components/utils/LoadingButton';
import pickObj from 'lodash/pick';
import ModalJS from '@/components/utils/Modal';
import Modal from '@/classes/Modal';
import VueTagsInput from '@johmun/vue-tags-input';
import EDUCATION_PHASES from '@/config/educationPhases';

export default {
  name: 'challengeOrganisation',
  components: {
    Wrapper,
    ConfirmationButton,
    LoadingButton,
    VueTagsInput,
    ModalJS, // eslint-disable-line
    modal: ModalJS,
  },
  data() {
    return {
      organisation: null,

      isEditingSchool: false,
      isSavingSchool: false,
      organisationEditable: null,

      isLookingUpPostcode: false,
      lookedUpPostcode: null,
      originalPostcode: null,

      fonettiSchools: [
        // { id: 1, text: 'Acme School' },
        // { id: 300, text: 'Some other school' },
      ],
      fonettiSchoolSearchString: '',
      selectedFonettiSchool: [],

      showConfirmationModal: {
        'reset-password': false,
      },

      isResetting: false,
      sendPasswordEmail: false,
      newPassword: null,

      isRegenerating: false,

      englishHubs: [],
      countries: [],
      schoolTypes: {
        independent: 'Independent',
        mat: 'Multi-Academy Trust',
        la: 'Local Authority',
        other: 'Other',
      },
      educationPhases: EDUCATION_PHASES,

      deletionString: null,
      modal: new Modal({
        deleteOrg: false,
      }),
    };
  },
  async created() {
    const payload = {
      challengeSlug: this.$route.params.challengeId,
      orgId: this.$route.params.orgId,
    };
    this.$store.dispatch('challenges/getChallengeOrganisation', payload).then((organisation) => {
      this.organisation = organisation;
    }).catch((err) => {
      this.$apiResponse.renderErrorMessage(err);
    });
    this.getCountries();
  },
  computed: {
    // Normalised status key used to colour the badge; falls back to 'unknown'
    // so an unexpected value still gets neutral styling rather than no class.
    assetStatus() {
      const raw = (this.organisation && this.organisation.assets_generation_status) || '';
      const known = ['complete', 'generating', 'pending', 'failed', 'error'];
      const value = raw.toString().toLowerCase();
      return known.includes(value) ? value : 'unknown';
    },
    // organisation.assets is mostly { key: url } but some entries are nested
    // objects (e.g. assembly_presentation: { younger: { pdf, pptx }, older: {...} }).
    // Group each top-level key into a row with a humanized name and one link per
    // leaf URL, pulling the file type (and a matching icon) out of the extension.
    assetGroups() {
      const titleCase = (s) => s
        .replace(/[_\s]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
      const typeFromUrl = (url) => {
        const match = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(url || '');
        return match ? match[1].toUpperCase() : 'FILE';
      };
      const iconForType = (type) => {
        if (type === 'PDF') return 'fa-file-pdf-o';
        if (type === 'PPTX' || type === 'PPT') return 'fa-file-powerpoint-o';
        if (['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP'].includes(type)) return 'fa-file-image-o';
        return 'fa-file-o';
      };
      const assets = this.organisation ? this.organisation.assets || {} : {};
      return Object.keys(assets).map((key) => {
        const value = assets[key];
        // subgroups keyed by their variant label ('' for flat assets), so a
        // nested asset like assembly_presentation renders as
        // "Younger: PDF PPTX" / "Older: PDF PPTX" rather than four wide buttons.
        const subgroups = {};
        const order = [];
        const walk = (node, labelParts) => {
          if (typeof node === 'string') {
            const type = typeFromUrl(node);
            // Drop a trailing label part that just repeats the file type
            // (e.g. assembly_presentation.younger.pdf -> "Younger", button PDF).
            const parts = labelParts.filter(
              (p, idx) => !(idx === labelParts.length - 1 && p.toUpperCase() === type),
            );
            const label = parts.map(titleCase).join(' ');
            if (!subgroups[label]) {
              subgroups[label] = { label, items: [] };
              order.push(label);
            }
            subgroups[label].items.push({ type, icon: iconForType(type), url: node });
          } else if (node && typeof node === 'object') {
            Object.keys(node).forEach((k) => walk(node[k], [...labelParts, k]));
          }
        };
        walk(value, []);
        return {
          name: titleCase(key),
          // nested when there are real variant labels (e.g. Younger/Older);
          // flat assets have a single '' label and render inline.
          nested: order.some((l) => l !== ''),
          subgroups: order.map((l) => subgroups[l]),
        };
      }).filter((group) => group.subgroups.length > 0);
    },
    governanceDescription() {
      let body = 'Other';
      switch (this.organisation.school_type) {
        case 'la': body = 'Local Authority'; break;
        case 'mat': body = 'Multi-Academy Trust'; break;
        case 'independant': body = 'Independant'; break;
        default: body = 'Other';
      }

      const bodyName = this.organisation.school_governed_by_name ?? '';

      return body + (bodyName.length > 0 ? ' - ' + bodyName : ''); // eslint-disable-line
    },

    regions() {
      const selectedCountry = this.countries.find((country) => country.id === this.organisationEditable.country);
      return selectedCountry && selectedCountry.regions ? selectedCountry.regions.groups : [];
    },

    // Nothing re-derives region/city on save any more, so warn when the postcode has been
    // edited without a lookup; the admin can either look it up or set the fields by hand.
    postcodeNeedsLookup() {
      const postcode = this.organisationEditable?.postcode || null;
      return postcode !== this.originalPostcode && postcode !== this.lookedUpPostcode;
    },

    filteredFonettiSchools() {
      return this.selectedFonettiSchool.length > 0 ? [] : this.fonettiSchools.filter((t) => new RegExp(this.fonettiSchoolSearchString, 'i').test(t.text)).slice(0, 10);
    },
  },
  methods: {
    handleResetPassword() {
      const action = 'reset-password';
      this.showConfirmationModal[action] = true;
    },
    async handleResetPasswordContinue() {
      this.isResetting = true;
      const payload = {
        challengeSlug: this.$route.params.challengeId,
        orgId: this.$route.params.orgId,
        data: {
          send_email: this.sendPasswordEmail,
        },
      };
      this.$store.dispatch('challenges/resetPassword', payload).then((newPassword) => {
        this.newPassword = newPassword;
      }).catch((err) => {
        this.$apiResponse.renderErrorMessage(err);
      }).finally(() => {
        this.isResetting = false;
      });
    },
    async getCountries() {
      try {
        this.countries = await this.$store.dispatch('app/fetchCountries');
      } catch (error) {
        this.$apiResponse.renderErrorMessage(error);
      }
    },
    async lookupPostcode() {
      this.isLookingUpPostcode = true;
      try {
        const { city, region } = await this.$store.dispatch('app/lookupPostcode', this.organisationEditable.postcode);
        this.organisationEditable.city = city;

        // An unrecognised region comes back null; keep whatever is already selected rather
        // than emptying a valid choice.
        if (region) {
          this.organisationEditable.region = region;
        }
        this.lookedUpPostcode = this.organisationEditable.postcode;
      } catch (error) {
        this.$apiResponse.renderErrorMessage(error);
      } finally {
        this.isLookingUpPostcode = false;
      }
    },
    async getEnglishHubs() {
      try {
        this.englishHubs = await this.$store.dispatch('challenges/getEnglishHubs', this.$route.params.challengeId);
      } catch (error) {
        this.$apiResponse.renderErrorMessage(error);
      }
    },
    async getSchoolsForAutocomplete() {
      try {
        const schools = await this.$store.dispatch('schools/getSchoolsForAutocomplete');
        this.fonettiSchools = schools.map((s) => {
          s.text = `[${s.id}] ${s.name}`;
          return s;
        });
        this.selectedFonettiSchool = this.fonettiSchools.filter((s) => s.id === this.organisationEditable?.enterprise_id);
      } catch (error) {
        this.$apiResponse.renderErrorMessage(error);
      }
    },
    startEditingSchool() {
      // Build the whole editable object in one assignment so every property is
      // reactive from the outset. Adding properties after the object is already
      // reactive (Vue 2) leaves them untracked, so the referral checkbox's v-if
      // would not react to the hub dropdown changing until a re-render.
      this.originalPostcode = this.organisation.postcode ?? null;
      this.lookedUpPostcode = null;
      this.organisationEditable = {
        ...pickObj(this.organisation, ['name', 'contact_name', 'contact_job_role', 'email', 'country', 'region', 'city', 'postcode', 'school_type', 'education_phase', 'school_governed_by_name']),
        enterprise_id: this.organisation.enterprise?.id,
        english_hub_slug: this.organisation.english_hub?.slug ?? null,
        english_hub_is_referral: this.organisation.english_hub?.is_referral
          ?? this.organisation.english_hub_referral_suggested ?? false,
      };
      this.getSchoolsForAutocomplete();
      this.getEnglishHubs();
      this.isEditingSchool = true;
    },
    async validateEditSchoolForm() {
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          await this.submitEditSchool();
        }
      });
    },
    async submitEditSchool() {
      this.isSavingSchool = true;

      // If not LA or MAT, clear out the governed by name
      if (!(['la', 'mat'].includes(this.organisationEditable.school_type))) {
        this.organisationEditable.school_governed_by_name = null;
      }

      this.organisationEditable.english_hub = {
        slug: this.organisationEditable.english_hub_slug ?? null,
        is_referral: !!this.organisationEditable.english_hub_is_referral,
      };
      delete this.organisationEditable.english_hub_slug;
      delete this.organisationEditable.english_hub_is_referral;

      const payload = {
        challengeSlug: this.$route.params.challengeId,
        orgId: this.$route.params.orgId,
        data: this.organisationEditable,
      };
      this.$store.dispatch('challenges/updateChallengeOrganisation', payload).then((organisation) => {
        this.organisation = organisation;
        this.isEditingSchool = false;
      }).catch((err) => {
        this.$apiResponse.renderErrorMessage(err);
      }).finally(() => {
        this.isSavingSchool = false;
      });
    },
    async forceRegenAssets() {
      this.isRegenerating = true;
      const payload = {
        challengeSlug: this.$route.params.challengeId,
        orgId: this.$route.params.orgId,
      };
      this.$store.dispatch('challenges/challengeOrganisationRegenAssets', payload).then((organisation) => {
        this.organisation = organisation;
      }).catch((err) => {
        this.$apiResponse.renderErrorMessage(err);
      }).finally(() => {
        this.isRegenerating = false;
      });
    },
    deleteOrg() {
      this.$store.dispatch('app/setGlobalLoading', true);
      this.$store.dispatch('organisations/deleteOrganisation', {
        challengeSlug: this.$route.params.challengeId,
        orgId: this.$route.params.orgId,
      }).then(() => {
        this.$router.push(`/challenges/${this.$route.params.challengeId}?am=organisation_deleted`);
      }).catch((error) => {
        this.$apiResponse.renderErrorMessage(error);
      }).finally(() => {
        this.$store.dispatch('app/setGlobalLoading', false);
      });
    },
  },
  watch: {
    organisation() {
      this.$route.meta.title = this.organisation.name;
      this.$route.meta.description = this.organisation.code;
    },
    selectedFonettiSchool(newValue) {
      console.log(newValue);
      this.organisationEditable.enterprise_id = newValue.length > 0 ? newValue[0].id : null;
    },
  },
};
</script>
<style lang="scss" scoped>
.asset-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.asset-status {
  color: #224450;
  font-size: 1.25rem;
}
.asset-status__badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 4px;
  padding: 3px 12px;
  border-radius: 14px;
  font-size: 1.15rem;
  font-weight: 600;
  text-transform: capitalize;
  background: #e4e9f0;
  color: #6c7a89;
}
.asset-status__badge--complete { background: #e3f1e4; color: #3c763d; }
.asset-status__badge--generating,
.asset-status__badge--pending { background: #fdf3e3; color: #b06a1c; }
.asset-status__badge--failed,
.asset-status__badge--error { background: #fbe6e6; color: #a94442; }
.asset-empty {
  text-align: center;
  color: #98a3ac;
  font-style: italic;
  padding: 20px 0 4px;
  margin: 0;
}
.asset-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  column-gap: 32px;
  border-top: 1px solid #edf0f5;
}
.asset-row {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 4px;
  padding: 9px 2px;
  border-bottom: 1px solid #edf0f5;
}
.asset-row__name {
  font-weight: 600;
  color: #224450;
  font-size: 1.25rem;
  white-space: nowrap;
}
.asset-row__links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  justify-content: flex-end;
}
// Nested groups (e.g. Assembly Presentation) stack their variant sub-rows below
// the group name so the name gets the full row width and never wraps.
.asset-row--nested {
  flex-direction: column;
  align-items: stretch;
}
.asset-row__subrows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 2px 0 2px 20px;
}
.asset-subrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.asset-subrow__label {
  color: #6c7a89;
  font-size: 1.25rem;
  min-width: 70px;
}
.asset-subrow__links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.asset-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 4px;
  background: #f3f6fa;
  color: #337ab7;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.asset-link:hover {
  background: #337ab7;
  color: #fff;
  text-decoration: none;
}
.asset-link .fa {
  font-size: 0.95em;
}
.asset-link--pdf { color: #d9534f; }
.asset-link--pptx,
.asset-link--ppt { color: #e07b39; }
.asset-link--png,
.asset-link--jpg,
.asset-link--jpeg,
.asset-link--gif { color: #5b8a4f; }
.asset-link--pdf:hover { background: #d9534f; color: #fff; }
.asset-link--pptx:hover,
.asset-link--ppt:hover { background: #e07b39; color: #fff; }
.asset-link--png:hover,
.asset-link--jpg:hover,
.asset-link--jpeg:hover,
.asset-link--gif:hover { background: #5b8a4f; color: #fff; }
</style>
